import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { brandGradient } from '../theme/tokens';

const FEATURES = [
  { icon: <FlightTakeoffOutlinedIcon fontSize="small" />, label: 'Uçuş Rezervasyonu' },
  { icon: <HotelOutlinedIcon fontSize="small" />, label: 'Otel Yönetimi' },
  { icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />, label: 'Transfer & Araç' },
  { icon: <InsightsOutlinedIcon fontSize="small" />, label: 'Anlık Raporlar' },
];

export interface AuthLayoutProps {
  heading: ReactNode;
  subheading?: ReactNode;
  children: ReactNode;
  /** Formun altındaki bağlantılar / yardımcı metin. */
  footer?: ReactNode;
}

/** Giriş / OTP / şifre-sıfırlama ekranlarının ortak kabuğu — solda marka paneli
 *  (tek onaylı gradient), sağda ortalı kart. `xs`'te yalnız kart. */
export function AuthLayout({ heading, subheading, children, footer }: AuthLayoutProps) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* SOL — MARKA PANELİ */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          px: 8,
          backgroundImage: brandGradient,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(circle at 78% 18%, rgba(255,255,255,0.10) 0%, transparent 45%), radial-gradient(circle at 12% 88%, rgba(0,0,0,0.16) 0%, transparent 42%)',
          }}
        />
        <Box sx={{ position: 'relative', maxWidth: 460 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 3 }}>
            <TravelExploreOutlinedIcon />
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.3px' }}>
              TravelFuture
            </Typography>
          </Stack>
          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Operasyonunuzu tek panelden yönetin
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, mt: 2 }}>
            Uçuş, otel, transfer ve araç kiralama süreçlerinizi tek platformdan yürütün; gerçek
            zamanlı takip ve anlık raporlarla kontrolü elden bırakmayın.
          </Typography>
          <Stack direction="row" useFlexGap sx={{ gap: 1, mt: 4, flexWrap: 'wrap' }}>
            {FEATURES.map((f) => (
              <Stack
                key={f.label}
                direction="row"
                spacing={0.75}
                sx={{
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 5,
                  fontSize: 13,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                {f.icon}
                <span>{f.label}</span>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* SAĞ — FORM KARTI */}
      <Box
        sx={{
          width: { xs: '100%', md: 480 },
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderLeft: { md: '1px solid' },
          borderColor: { md: 'divider' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: { xs: 5, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380, mx: 'auto' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            {heading}
          </Typography>
          {subheading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 4 }}>
              {subheading}
            </Typography>
          )}
          <Box sx={{ mt: subheading ? 0 : 4 }}>{children}</Box>
          {footer && <Box sx={{ mt: 4 }}>{footer}</Box>}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 5 }}
          >
            © {new Date().getFullYear()} TravelFuture — Kurumsal Seyahat Paneli
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
